import Paper from '@material-ui/core/Paper'
import Slide from '@material-ui/core/Slide'
import React, { ReactElement, ReactNode, useContext, useState } from 'react'
import { FieldsList as FieldsListLanguage } from '../../../../i18n'
import LanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import Checkbox from '../../Common/Components/Checkbox'
import Heading from '../../Common/Components/Heading'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'
import Icon, { IconName } from '../../Common/Components/Icon'
import IconButton, { IconButtonType } from '../../Common/Components/IconButton'
import Ul, { Li } from '../../Common/Components/Ul'
import { REPORT_STAT_FLAGS_TYPE_NONE } from '../../Common/Constants/ReportStatFlags'
import { LanguageContext } from '../../Common/Contexts'
import { RptFilterEntityInfo } from '../../Common/Types/Types'
import { hasKey } from '../../Common/Utils'
import { AddMenuItem, buildMenuStructureFromAvailableStats } from './BuildAddMenuUtils'
import { AvailableStatisticsFieldsArray, StatisticsConfiguration, StatProps } from './Types'
import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import { ReportTypes } from '../../../NonReact/constants/ReportTypes'
import { ENTITY_STAT_FIXED_FIELDS } from '../Constants'
import { ArrayUtils } from '../../../NonReact/utils/ArrayUtils'

export type FieldsAddProps = {
    statsList: AvailableStatisticsFieldsArray
    configuredStatisticsConfigurations: StatisticsConfiguration[]
    addStatistic: (statistic: StatProps) => void
    removeStatistic: (statistic: StatProps) => void
    rptFilterEntityInfo: RptFilterEntityInfo | undefined
    isUserLicensedForCurrentReportStyle: boolean
    selectedReportStyle: Definable<ReportTypes>
}

type MenuStateItemProps = {
    active: boolean
    name: string
    title: string
    path: string
}

type MenuStateProps = {
    [uid: string]: MenuStateItemProps
}

/**
 * Fields tab in report configuration modal window.
 *
 * @param statsList List of all statistics available.
 * @param configuredStatisticsConfigurations List of currently selected statistics.
 * @param addStatistic Callback metod to add new statistics to list of selected statistics.
 * @param removeStatistic Callback method to remove statistics from list of selected statistics.
 * @param rptFilterEntityInfo Up-to-date report filter entity info object containing report capabilities.
 * @param isUserLicensedForCurrentReportStyle Flag specifying whether user is licensed for current report style.
 */
const FieldsListAdd: React.FC<FieldsAddProps> = ({
    statsList,
    configuredStatisticsConfigurations,
    addStatistic,
    removeStatistic,
    rptFilterEntityInfo,
    isUserLicensedForCurrentReportStyle,
    selectedReportStyle
}): ReactElement => {
    const [menuState, setMenuState] = useState<MenuStateProps | []>([])
    const [history, setHistory] = useState<string[]>([])
    const [breadcrumb, setBreadcrumb] = useState<string[] | []>([])
    const [, forceUpdate] = useState({})

    const language = useContext(LanguageContext) as LanguageHandler
    language.loadBundle('FieldsList')

    const getStatInfo = (id: number): StatProps | undefined => statsList.find((stat) => stat.StatID === id)

    const handleShowSubMenu = (e: React.MouseEvent<HTMLAnchorElement>, name: string, uid: string): void => {
        e.preventDefault()

        setMenuState((prev) => {
            const newArray = prev as MenuStateProps

            if (hasKey(newArray, uid) && newArray[uid]) {
                newArray[uid].active = true
            }

            return newArray
        })

        setBreadcrumb((prev) => {
            const newBreadcrumb = prev as string[]

            newBreadcrumb.push(name)

            return newBreadcrumb
        })

        setHistory((prev) => {
            const newHistory = prev

            if (!hasKey(newHistory, uid)) {
                newHistory.push(uid)
            }

            return newHistory
        })

        forceUpdate({})
    }

    const hideSubMenu = (uid: string): void => {
        setMenuState((prev) => {
            const newArray = prev as MenuStateProps

            if (hasKey(newArray, uid) && newArray[uid]) {
                newArray[uid].active = false
            }

            return newArray
        })

        setBreadcrumb((prev) => {
            let newBreadcrumbs = prev

            // Remove last array item or clear all
            if (newBreadcrumbs.length > 1) {
                newBreadcrumbs.pop()
            } else {
                newBreadcrumbs = []
            }
            return newBreadcrumbs
        })

        forceUpdate({})
    }

    const handleMenuBack = (): void => {
        const previousMenu = history.pop()

        if (previousMenu) {
            hideSubMenu(previousMenu)
        }
    }

    const buildListSlide = (): ReactNode => {
        const buildMenuItem = (statIds: number[], key: string): ReactNode =>
            statIds?.map((statId: number, i: number): ReactNode => {
                const uid = key + i + statId
                const statInfo = getStatInfo(statId)

                const checked = !!configuredStatisticsConfigurations.find(
                    (configuredStatisticsConfiguration) => configuredStatisticsConfiguration.ID === statInfo?.StatID
                )

                // Check whether user is licensed for this field.
                let licensed = false
                if (
                    isUserLicensedForCurrentReportStyle &&
                    rptFilterEntityInfo &&
                    statInfo &&
                    (Number(rptFilterEntityInfo.EntityInfo.ReportCapabilityInfo.StatFlags) & statInfo?.StatFlagsType) >
                        REPORT_STAT_FLAGS_TYPE_NONE
                ) {
                    licensed = true
                }

                return (
                    <Li key={uid} className={`stat-id-${statId}`}>
                        <Checkbox
                            labelRight={statInfo?.MediumName}
                            isChecked={checked}
                            onChange={(): void => {
                                if (statInfo) {
                                    if (checked) {
                                        removeStatistic(statInfo)
                                    } else {
                                        addStatistic(statInfo)
                                    }
                                }
                            }}
                            fullWidth
                            disabled={
                                selectedReportStyle === ReportTypes.CALLS_BY_INTERVAL_AND_ENTITY &&
                                ArrayUtils.includesAny([statInfo?.StatID ?? EMPTY_STRING], ENTITY_STAT_FIXED_FIELDS)
                            }
                            title={
                                licensed
                                    ? statInfo?.LongName
                                    : `${language?.get(FieldsListLanguage.UnlicensedPrefix)} ${statInfo?.LongName}`
                            }
                        />
                    </Li>
                )
            })

        const buildMenu = (menu: AddMenuItem[], key: string, currentMenuDepth: number): ReactNode => {
            const defaultZIndex = 10
            const zIndexValue = defaultZIndex + currentMenuDepth

            return menu.map((menuItem: AddMenuItem, index: number): ReactNode => {
                const menuAll: ReactNode[] = []
                const uid = key + index + (menuItem?.name ?? EMPTY_STRING)

                let show = false

                if (hasKey(menuState, uid)) {
                    const itemState = menuState[uid] as MenuStateItemProps
                    show = itemState.active
                } else {
                    setMenuState(
                        (prev) =>
                            ({
                                ...prev,
                                [uid]: {
                                    active: false,
                                    name: menuItem?.name,
                                    title: menuItem?.title,
                                    path: ''
                                }
                            }) as MenuStateProps
                    )
                }

                if (menuItem?.name) {
                    menuAll.push(
                        <Li key={uid}>
                            <a
                                href='#'
                                onClick={(e): void => handleShowSubMenu(e, menuItem?.name || '', uid)}
                                className={`submenu-link ${menuItem?.customClass ?? ''}`}
                                title={menuItem?.title}
                            >
                                {menuItem?.name}
                                <Icon iconName={IconName.ARROW_RIGHT}></Icon>
                            </a>

                            <Slide direction='left' in={show} mountOnEnter unmountOnExit>
                                <Paper elevation={4} style={{ zIndex: zIndexValue }}>
                                    <Ul key={uid} className='menu-wrapper'>
                                        {menuItem?.subMenuItems &&
                                            buildMenu(menuItem?.subMenuItems as [], uid, currentMenuDepth + 1)}
                                    </Ul>
                                </Paper>
                            </Slide>
                        </Li>
                    )
                }

                if (menuItem?.statIds) {
                    menuAll.push(buildMenuItem(menuItem?.statIds as [], uid))
                }
                return menuAll
            })
        }

        const list = buildMenu(buildMenuStructureFromAvailableStats(language, statsList), '', 0)

        return list
    }

    let navTitle = language?.get(FieldsListLanguage.FieldsList_AddMenuTitle)

    if (breadcrumb.length > 1) {
        navTitle = breadcrumb.join(' / ')
    } else if (breadcrumb.length === 1) {
        navTitle = breadcrumb[0]
    }

    return (
        <div className='fields-list-add'>
            <div className='fields-list-add-nav'>
                {history.length > 0 && (
                    <IconButton
                        onClick={handleMenuBack}
                        iconName={IconName.ARROW_BACK}
                        type={IconButtonType.BUTTON}
                    ></IconButton>
                )}
                <Heading level={HeadingLevel.THREE} className='title'>
                    {navTitle}
                </Heading>
            </div>
            <Ul className='fields-list-add-menu'>
                {statsList.length > 0 ? (
                    buildListSlide()
                ) : (
                    <Li>{language.get(FieldsListLanguage.NoFieldsAvailable)}</Li>
                )}
            </Ul>
        </div>
    )
}
export default FieldsListAdd
